import React, { useEffect, useState } from 'react';
import { X, Wine, ChevronRight } from 'lucide-react';
import { apiService } from '@/services/api';

interface Props {
  branchId: string;
  branchName?: string;
  onClose: () => void;
}

interface Pkg { id: string; name: string; sectionName?: string | null; minSpend: number; depositAmount: number; maxGuests?: number | null; perks?: string | null; bottles?: Array<{ name: string; price?: number | null }> | null }
interface Res { id: string; sectionName?: string | null; reservationDate: string; arrivalTime?: string | null; partySize: number; minSpend: number; status: string }

const money = (n: number) => `${Math.round(Number(n) || 0)}`;
const todayStr = () => new Date().toISOString().slice(0, 10);

const VipReserve: React.FC<Props> = ({ branchId, branchName, onClose }) => {
  const [packages, setPackages] = useState<Pkg[]>([]);
  const [mine, setMine] = useState<Res[]>([]);
  const [loading, setLoading] = useState(true);
  const [chosen, setChosen] = useState<Pkg | null>(null);
  const [date, setDate] = useState(todayStr());
  const [time, setTime] = useState('');
  const [party, setParty] = useState(2);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [p, m] = await Promise.all([
        apiService.getVipPackages(branchId).then((r: any) => r?.packages || []).catch(() => []),
        apiService.getMyVipReservations().then((r: any) => (r?.reservations || []).filter((x: Res) => true)).catch(() => []),
      ]);
      setPackages(p); setMine(m);
    } finally { setLoading(false); }
  };
  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [branchId]);

  const submit = async () => {
    if (busy) return;
    setBusy(true); setNote('');
    try {
      await apiService.createVipReservation(branchId, {
        packageId: chosen?.id, reservationDate: date, arrivalTime: time || undefined,
        partySize: party, notes: notes || undefined,
      });
      setChosen(null); setNotes(''); setTime('');
      setNote('Sent! The venue will confirm your VIP table shortly.');
      await load();
    } catch (e: any) { setNote(e?.message || 'Could not send your request.'); }
    finally { setBusy(false); }
  };

  const statusStyle = (s: string): React.CSSProperties => {
    const map: Record<string, [string, string]> = {
      requested: ['#fef3c7', '#92400e'], confirmed: ['#dcfce7', '#166534'], seated: ['#dcfce7', '#166534'],
      completed: ['#e5e7eb', '#374151'], cancelled: ['#fee2e2', '#991b1b'], no_show: ['#fee2e2', '#991b1b'],
    };
    const [bg, fg] = map[s] || ['#e5e7eb', '#374151'];
    return { background: bg, color: fg };
  };

  return (
    <div style={overlay} onClick={onClose} role="dialog" aria-label="Bottle service">
      <div style={sheet} onClick={(e) => e.stopPropagation()}>
        <div style={header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Wine size={18} color="#e0531f" /><b>{branchName ? `${branchName} · VIP` : 'Bottle service'}</b></div>
          <button type="button" onClick={onClose} style={iconBtn} aria-label="Close"><X size={20} /></button>
        </div>

        <div style={body}>
          {note && <div style={{ fontSize: '0.86rem', color: note.startsWith('Sent') ? '#166534' : '#b91c1c' }}>{note}</div>}

          {loading ? <p style={muted}>Loading…</p> : chosen ? (
            <>
              <button style={backLink} onClick={() => setChosen(null)}>← Choose a different table</button>
              <div style={pkgCard}>
                <div style={{ fontWeight: 800 }}>{chosen.name}</div>
                <div style={pkgSub}>{chosen.sectionName || 'VIP section'} · minimum spend {money(chosen.minSpend)}{Number(chosen.depositAmount) > 0 ? ` · deposit ${money(chosen.depositAmount)}` : ''}</div>
                {chosen.perks && <div style={{ fontSize: '0.82rem', color: '#374151', marginTop: 4 }}>{chosen.perks}</div>}
                {Array.isArray(chosen.bottles) && chosen.bottles.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: '0.76rem', fontWeight: 700, color: '#9a6a54', marginBottom: 3 }}>🍾 Bottle menu</div>
                    {chosen.bottles.map((b, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.84rem', color: '#374151', padding: '2px 0' }}>
                        <span>{b.name}</span>
                        {b.price != null && <span style={{ fontWeight: 600 }}>{money(b.price)}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <label style={lbl}>Date</label>
              <input style={input} type="date" min={todayStr()} value={date} onChange={(e) => setDate(e.target.value)} />
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1 }}><label style={lbl}>Arrival (optional)</label><input style={input} type="time" value={time} onChange={(e) => setTime(e.target.value)} /></div>
                <div style={{ flex: 1 }}><label style={lbl}>Guests</label><input style={input} type="number" min={1} max={chosen.maxGuests || 30} value={party} onChange={(e) => setParty(Math.max(1, Number(e.target.value) || 1))} /></div>
              </div>
              <label style={lbl}>Anything we should know? (optional)</label>
              <textarea style={{ ...input, minHeight: 54 }} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Birthday, preferred bottles, etc." />
              <button style={cta} disabled={busy} onClick={submit}>{busy ? 'Sending…' : 'Request this VIP table'}</button>
              <p style={{ ...muted, fontSize: '0.75rem', padding: 0 }}>The venue confirms your table — you're not charged now.</p>
            </>
          ) : (
            <>
              {mine.length > 0 && (
                <div style={{ marginBottom: 6 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 6 }}>Your VIP reservations</div>
                  {mine.map((r) => (
                    <div key={r.id} style={mineRow}>
                      <span style={{ flex: 1, fontSize: '0.84rem' }}>{r.sectionName || 'VIP table'} · {r.reservationDate}{r.arrivalTime ? ` ${r.arrivalTime}` : ''} · {r.partySize} guests</span>
                      <span style={{ ...badge, ...statusStyle(r.status) }}>{r.status.replace('_', ' ')}</span>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>Reserve a table</div>
              {packages.length === 0 ? (
                <p style={muted}>No VIP tables available right now.</p>
              ) : packages.map((p) => (
                <button key={p.id} style={pkgBtn} onClick={() => setChosen(p)}>
                  <span style={{ flex: 1, textAlign: 'left' }}>
                    <span style={{ display: 'block', fontWeight: 700 }}>{p.name}</span>
                    <span style={pkgSub}>{p.sectionName || 'VIP section'} · min spend {money(p.minSpend)}{p.maxGuests ? ` · up to ${p.maxGuests}` : ''}</span>
                    {p.perks && <span style={{ display: 'block', fontSize: '0.78rem', color: '#6b7280' }}>{p.perks}</span>}
                  </span>
                  <ChevronRight size={18} color="#e0531f" />
                </button>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 3000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' };
const sheet: React.CSSProperties = { width: '100%', maxWidth: 520, maxHeight: '92vh', display: 'flex', flexDirection: 'column', background: '#fff', color: '#111827', borderTopLeftRadius: 20, borderTopRightRadius: 20 };
const header: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid #f0f0f0' };
const iconBtn: React.CSSProperties = { width: 36, height: 36, borderRadius: 999, border: '1px solid #e5e7eb', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' };
const body: React.CSSProperties = { padding: 16, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 };
const muted: React.CSSProperties = { color: '#6b7280', fontSize: '0.9rem', textAlign: 'center', padding: '1.2rem 0' };
const pkgBtn: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '12px 14px', borderRadius: 12, border: '1px solid #f0e6e0', background: '#fff7f4', cursor: 'pointer', color: '#111827' };
const pkgCard: React.CSSProperties = { padding: '12px 14px', borderRadius: 12, border: '1px solid #f0e6e0', background: '#fff7f4' };
const pkgSub: React.CSSProperties = { display: 'block', fontSize: '0.8rem', color: '#9a6a54' };
const backLink: React.CSSProperties = { alignSelf: 'flex-start', background: 'none', border: 'none', color: '#e0531f', cursor: 'pointer', fontSize: '0.85rem', padding: 0 };
const lbl: React.CSSProperties = { fontSize: '0.78rem', color: '#6b7280', fontWeight: 600 };
const input: React.CSSProperties = { padding: '10px 12px', borderRadius: 10, border: '1px solid #e5e7eb', fontSize: '0.9rem', width: '100%', boxSizing: 'border-box' };
const cta: React.CSSProperties = { padding: '13px', borderRadius: 12, border: 'none', background: '#e0531f', color: '#fff', fontWeight: 700, cursor: 'pointer' };
const mineRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' };
const badge: React.CSSProperties = { fontSize: '0.68rem', fontWeight: 700, padding: '2px 8px', borderRadius: 999, textTransform: 'capitalize' };

export default VipReserve;
